import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import ModalShell from "components/ModalShell";

// Generic create/edit dialog driven by a `fields` array — replaces window.prompt().
// Each field: { name, label, type?, options?, required?, helperText? }.
// `type` is a native input type ("text", "email", "password", "number") or "select".
function EntityFormDialog({
  open,
  title,
  fields,
  initialValues,
  validationSchema,
  submitText,
  loading,
  onSubmit,
  onClose,
}) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});

  // Reset the form whenever it (re)opens or the seed values change.
  useEffect(() => {
    setValues(initialValues);
    setErrors({});
  }, [initialValues, open]);

  const handleChange = (name) => (event) =>
    setValues((current) => ({ ...current, [name]: event.target.value }));

  const handleSubmit = async () => {
    if (validationSchema) {
      try {
        await validationSchema.validate(values, { abortEarly: false });
        setErrors({});
      } catch (err) {
        const fieldErrors = {};
        (err.inner || []).forEach((e) => {
          if (e.path && !fieldErrors[e.path]) fieldErrors[e.path] = e.message;
        });
        setErrors(fieldErrors);
        return;
      }
    }
    await onSubmit(values);
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <MDButton onClick={onClose} color="secondary" variant="text" disabled={loading}>
            Cancel
          </MDButton>
          <MDButton onClick={handleSubmit} color="info" variant="gradient" disabled={loading}>
            {loading ? "Saving..." : submitText}
          </MDButton>
        </>
      }
    >
      <MDBox display="flex" flexDirection="column" gap={2.5} mt={1}>
        {fields.map((field) => (
          <TextField
            key={field.name}
            label={field.label}
            type={field.type === "select" ? undefined : field.type || "text"}
            select={field.type === "select"}
            value={values[field.name] ?? ""}
            onChange={handleChange(field.name)}
            error={Boolean(errors[field.name])}
            helperText={errors[field.name] || field.helperText || ""}
            required={field.required}
            fullWidth
          >
            {field.type === "select" &&
              (field.options || []).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </TextField>
        ))}
      </MDBox>
    </ModalShell>
  );
}

EntityFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({ value: PropTypes.any, label: PropTypes.string })
      ),
      required: PropTypes.bool,
      helperText: PropTypes.string,
    })
  ).isRequired,
  initialValues: PropTypes.object,
  validationSchema: PropTypes.object,
  submitText: PropTypes.string,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

EntityFormDialog.defaultProps = {
  initialValues: {},
  validationSchema: null,
  submitText: "Save",
  loading: false,
};

export default EntityFormDialog;
